import { Card, IconSvg, Main_Plain } from 'monica-alexandria';
import styled from 'styled-components';

const SassBoardCard = styled(Card)`
    padding: 1.2rem;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    background: linear-gradient(180deg, ${p => p.theme.background} 0%, ${p => p.theme.low} 100%);
    cursor: pointer;
    border-top: 0.4rem solid var(--focus);

    &[data-dragging="true"] {
        opacity: 0.45;
    }

    &:hover {
        box-shadow: ${p => p.theme.outFocus};
    }
`;

const SassBoardDragHandle = styled.div`
    position: absolute;
    top: 0.8rem;
    right: 0.8rem;
    z-index: 3;
    width: 3.2rem;
    height: 3.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--smallRadius);
    background: ${p => p.theme.background};
    box-shadow: ${p => p.theme.out};
    cursor: grab;
    user-select: none;
    touch-action: none;

    svg {
        width: 1.8rem;
        fill: ${p => p.theme.color};
    }

    &:active {
        cursor: grabbing;
    }
`;

const SassBoardCardHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const SassBoardCardBadges = styled.div`
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
`;

const SassBoardCardChip = styled.div`
    width: fit-content;
    padding: 0.2rem 0.5rem;
    border-radius: var(--smallRadius);
    border: 0.1rem solid var(--focus);
    background: color-mix(in srgb, var(--focus) 18%, transparent);

    &[data-tone="error"] {
        border-color: var(--error);
        background: color-mix(in srgb, var(--error) 18%, transparent);
    }

    &[data-tone="warning"] {
        border-color: var(--warning);
        background: color-mix(in srgb, var(--warning) 18%, transparent);
    }

    &[data-tone="success"] {
        border-color: var(--success);
        background: color-mix(in srgb, var(--success) 18%, transparent);
    }

    h6 {
        margin: 0;
        color: var(--focus);
        font-family: "fontBold";
    }

    &[data-tone="error"] h6 {
        color: var(--error);
    }

    &[data-tone="warning"] h6 {
        color: var(--warning);
    }

    &[data-tone="success"] h6 {
        color: var(--success);
    }
`;

const SassBoardCardCodes = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 0.4rem;
    flex-wrap: wrap;
`;

const SassBoardCardCode = styled.div`
    padding: 0.2rem 0.5rem;
    border-radius: var(--smallRadius);
    border: 0.1rem solid var(--mainDark);
    background: color-mix(in srgb, var(--mainDark) 18%, transparent);

    h6 {
        margin: 0;
        color: var(--mainDark);
        font-family: "fontBold";
    }
`;

const SassBoardCardBottom = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.8rem;
`;

const SassBoardCardSeller = styled.div`
    display: flex;
    align-items: center;
    gap: 0.6rem;
    flex-shrink: 0;

    svg {
        width: 1.4rem;
        fill: ${p => p.theme.high};
    }
`;

const SassBoardCardMeta = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    min-width: 0;

    h6 {
        margin: 0;
        color: ${p => p.theme.high};
        overflow-wrap: anywhere;
    }
`;

const SassBoardUnassigned = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--smallPads);
    padding: var(--smallPads);
    border-radius: var(--smallRadius);
    border: 0.1rem solid var(--warning);

    h6 {
        color: var(--warning);
    }
`;

export const Sass_Offer_Card = (props) => {
    const cardData = props.cardData;
    const SellerAvatar = props.SellerAvatar;
    const canDrag = props.canDrag === true;
    const cardIsDragging = cardData.offerId.length > 0 && props.dragOfferId === cardData.offerId;
    const callerAvatar = SellerAvatar && cardData.callerLabel !== '-';

    return (
        <SassBoardCard
            draggable={canDrag}
            data-dragging={cardIsDragging ? 'true' : 'false'}
            onClick={() => props.onOpen(props.offerItem)}
            onDragStart={canDrag ? (event) => props.onDragStart(event, cardData.offerId) : undefined}
            onDragEnd={canDrag ? props.onDragEnd : undefined}
        >
            {canDrag ? (
                <SassBoardDragHandle
                    role="button"
                    tabIndex={0}
                    title={props.t('Drag')}
                    onClick={(event) => event.stopPropagation()}
                    onMouseDown={(event) => props.onDragHandleDown(event, cardData.offerId)}
                    onPointerDown={(event) => props.onDragHandleDown(event, cardData.offerId)}
                >
                    <IconSvg Icon="Ham" />
                </SassBoardDragHandle>
            ) : null}
            <SassBoardCardHeader>
                <SassBoardCardBadges>
                    <SassBoardCardChip data-tone={cardData.meetingTone}>
                        <h6>{cardData.meetingDateLabel}</h6>
                    </SassBoardCardChip>
                    {cardData.linkedLabels.map((linkedLabel) => (
                        <SassBoardCardChip key={`${cardData.offerReference}_${linkedLabel}`} data-tone="success">
                            <h6>{linkedLabel}</h6>
                        </SassBoardCardChip>
                    ))}
                    {cardData.syncStatusLabel ? (
                        <SassBoardCardChip data-tone={cardData.syncStatusTone}>
                            <h6>{props.t(cardData.syncStatusLabel)}</h6>
                        </SassBoardCardChip>
                    ) : null}
                </SassBoardCardBadges>
                <h4>{cardData.brand}</h4>
            </SassBoardCardHeader>
            <SassBoardCardBottom>
                <SassBoardCardCodes>
                    {cardData.footerCodes.map((codeValue) => (
                        <SassBoardCardCode key={`${cardData.offerReference}_${codeValue}`}>
                            <h6>{codeValue}</h6>
                        </SassBoardCardCode>
                    ))}
                </SassBoardCardCodes>
                <SassBoardCardSeller>
                    {SellerAvatar ? <SellerAvatar seller={cardData.seller} /> : null}
                    {callerAvatar ? <IconSvg Icon="Next" /> : null}
                    {callerAvatar ? <SellerAvatar seller={cardData.caller} /> : null}
                </SassBoardCardSeller>
            </SassBoardCardBottom>
            {!cardData.caller ? (
                <SassBoardUnassigned>
                    <h6>{props.t('Unassigned')}</h6>
                    {props.canClaim ? (
                        <Main_Plain
                            size="small"
                            text={props.t('Assign to me')}
                            onClick={(event) => {
                                event.stopPropagation();
                                props.onClaim(props.offerItem);
                            }}
                        ></Main_Plain>
                    ) : null}
                </SassBoardUnassigned>
            ) : null}
        </SassBoardCard>
    );
};
